import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  TabBar, tabPanelProps, DataTable, Modal, Field, useModalClose, useConfirm, Chip, Avatar,
  ErrorState, EmptyState, Loading,
} from './workspace/shared';
import CountUp from './workspace/shared/CountUp';
import { apiFetch, apiErrorMessage } from './workspace/hooks/useApi';
import { useTabParam } from './workspace/hooks/useUrlState';
import { useOpenFromLink, useQueryFromLink } from './workspace/hooks/useDeepLink';
import { dueStatus } from './workspace/utils/compliance';
import { formatDate, plural, toLocalISODate } from './workspace/utils/format';
// Workspace styles are scoped under .cw-root; importing them here lets the legacy
// rollback dashboard (which also renders this component) style it too.
import './workspace/workspace.css';

/**
 * <ContractorEquipment /> — equipment & fleet register (workspace Equipment screen;
 * also rendered by the legacy rollback dashboard). Live data from
 * /api/contractor/equipment, /vehicles and /equipment/stats via apiFetch, so a 401
 * signs out cleanly and every failure shows a real error with Retry.
 */

const CATEGORIES = ['PPE', 'Power tools', 'Arms & weights'];
// Stored values are kept exactly as the API has them; labels are display-only.
const CATEGORY_VALUES = { PPE: 'PPE', 'Power tools': 'Power Tools', 'Arms & weights': 'Arms & Weights' };
const CATEGORY_LABEL = Object.fromEntries(Object.entries(CATEGORY_VALUES).map(([l, v]) => [v, l]));
const STATUS_VALUES = ['Pass', 'Fail', 'Not Present', 'NA', 'Scrapped'];
const STATUS_CHIP = {
  Pass: ['success', 'Pass'],
  Fail: ['danger', 'Fail'],
  'Not Present': ['warning', 'Not present'],
  NA: ['muted', 'Not applicable'],
  Scrapped: ['muted', 'Scrapped'],
};
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'equipment', label: 'All equipment' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'personnel', label: 'Personnel' },
];
const TAB_IDS = TABS.map((t) => t.id);

const EMPTY_EQUIP = {
  equipment_name: '', equipment_type: '', category: 'PPE', id_number: '',
  manufacturer: '', safe_working_load: '', batch_serial_number: '', colour: '',
  assigned_to: '', kit_id: '', vehicle_reg: '', location: '',
  date_in_service: '', last_inspection_date: '', next_inspection_date: '',
  pat_test_date: '', status: 'Pass', notes: '',
  certificate_of_conformity: '', user_instructions: '', coc_reference: '',
};
const EMPTY_VEHICLE = { vehicle_reg: '', vehicle_name: '', make: '', model: '', colour: '', kit_id: '', notes: '' };
const DATE_KEYS = ['date_in_service', 'last_inspection_date', 'next_inspection_date', 'pat_test_date'];
const dash = <span style={{ color: 'var(--cw-placeholder)' }} aria-label="Not recorded">—</span>;
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 'var(--cs-text-sm)' };

const StatusBadge = ({ status }) => {
  const [tone, label] = STATUS_CHIP[status] || ['muted', status || 'Unknown'];
  return <Chip tone={tone} label={label} dot strike={status === 'Scrapped'} />;
};

/** Next-inspection date with a worded status (Overdue / Due soon). Scrapped items never nag. */
const InspectionDate = ({ date, status }) => {
  if (!date) return dash;
  const s = dueStatus(date);
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {formatDate(date)}
      {status !== 'Scrapped' && s.level === 'overdue' && <Chip tone="danger" label="Overdue" />}
      {status !== 'Scrapped' && s.level === 'soon' && <Chip tone="warning" label="Due soon" />}
    </span>
  );
};

const CancelButton = ({ disabled, label = 'Cancel' }) => {
  const requestClose = useModalClose();
  return <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={disabled}>{label}</button>;
};
const FormError = ({ message }) => (message ? (
  <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}>
    <div className="cw-error-state-text"><p className="cw-error-state-title">Not saved</p><p className="cw-error-state-reason">{message}</p></div>
  </div>
) : null);

const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12 };
const rowBtn = {
  display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 16px', minHeight: 48,
  background: 'none', border: 'none', borderTop: '1px solid var(--cw-border-light)', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
};

/** A list of equipment as buttons (attention list, vehicle contents, person, location). */
const ItemList = ({ items, onOpen, showDue = true, empty }) => (
  items.length === 0 ? <EmptyState compact title={empty} /> : (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {items.map((item) => (
        <li key={item.id}>
          <button type="button" style={rowBtn} onClick={() => onOpen(item)}>
            <span style={{ flex: '1 1 200px', minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600 }}>{item.equipment_name}</span>
              <span className="cw-meta">{[item.equipment_type, item.id_number || 'No ID', item.assigned_to, item.vehicle_reg].filter(Boolean).join(' · ')}</span>
            </span>
            {showDue && <InspectionDate date={item.next_inspection_date} status={item.status} />}
            <StatusBadge status={item.status} />
            <i className="fa-solid fa-chevron-right" aria-hidden="true" style={{ color: 'var(--cw-placeholder)' }} />
          </button>
        </li>
      ))}
    </ul>
  )
);

export default function ContractorEquipment() {
  const { confirm } = useConfirm();
  const rootRef = useRef(null);
  const [inWorkspace, setInWorkspace] = useState(true);
  useLayoutEffect(() => { setInWorkspace(!!rootRef.current?.parentElement?.closest('.cw-root')); }, []);

  // Data
  const [equipment, setEquipment] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [staffNames, setStaffNames] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [vehiclesError, setVehiclesError] = useState(null);

  // Navigation
  const [tab, setTab] = useTabParam('tab', 'overview', { allowed: TAB_IDS });
  const [detailItem, setDetailItem] = useState(null);
  const [vehicleDetail, setVehicleDetail] = useState(null);
  const [personnelDetail, setPersonnelDetail] = useState(null);
  const [locationDetail, setLocationDetail] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  useQueryFromLink(setSearch);
  const [filterCat, setFilterCat] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  // Modals
  const [equipModal, setEquipModal] = useState(null);       // { editing, initial }
  const [equipForm, setEquipForm] = useState({ ...EMPTY_EQUIP });
  const [equipErrors, setEquipErrors] = useState({});
  const [formStep, setFormStep] = useState(0);
  const [assignNewPerson, setAssignNewPerson] = useState(false);
  const [vehicleModal, setVehicleModal] = useState(null);   // { editing, initial }
  const [vehicleForm, setVehicleForm] = useState({ ...EMPTY_VEHICLE });
  const [vehicleError, setVehicleError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bulk edit
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkField, setBulkField] = useState('assigned_to');
  const [bulkValue, setBulkValue] = useState('');

  // ─── Fetchers ───────────────────────────────
  const refreshAll = useCallback(async () => {
    const [eq, veh, st, staff] = await Promise.allSettled([
      apiFetch('/api/contractor/equipment'),
      apiFetch('/api/contractor/vehicles'),
      apiFetch('/api/contractor/equipment/stats'),
      apiFetch('/api/contractor/staff'),
    ]);
    if (eq.status === 'fulfilled') { setEquipment(Array.isArray(eq.value) ? eq.value : (eq.value?.equipment || [])); setLoadError(null); }
    else setLoadError(eq.reason);
    if (veh.status === 'fulfilled') { setVehicles(Array.isArray(veh.value) ? veh.value : (veh.value?.vehicles || [])); setVehiclesError(null); }
    else setVehiclesError(veh.reason);
    setStats(st.status === 'fulfilled' ? st.value : null);
    if (staff.status === 'fulfilled') {
      const list = Array.isArray(staff.value) ? staff.value : (staff.value?.staff || []);
      setStaffNames(list.map((s) => s.name || [s.first_name, s.last_name].filter(Boolean).join(' ')).filter(Boolean));
    }
  }, []);
  const reload = useCallback(async () => { setLoading(true); await refreshAll(); setLoading(false); }, [refreshAll]);
  useEffect(() => { reload(); }, [reload]);

  // ─── Derived data ───────────────────────────
  const live = useMemo(() => equipment.filter((e) => e.status !== 'Scrapped'), [equipment]);
  const filtered = useMemo(() => equipment.filter((e) => {
    if (filterCat !== 'All' && e.category !== filterCat) return false;
    if (filterStatus !== 'All' && e.status !== filterStatus) return false;
    if (search) {
      const s = search.toLowerCase();
      return [e.equipment_name, e.id_number, e.manufacturer, e.assigned_to, e.kit_id,
        e.equipment_type, e.vehicle_reg, e.location, e.batch_serial_number,
        e.colour, e.category, e.safe_working_load, e.notes, e.coc_reference]
        .some((v) => String(v || '').toLowerCase().includes(s));
    }
    return true;
  }), [equipment, filterCat, filterStatus, search]);

  // Drop selections that a filter has hidden, so bulk edits only touch what you can see.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(filtered.map((e) => e.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const byType = useMemo(() => {
    const m = {};
    live.forEach((e) => { const t = e.equipment_type || 'Other'; m[t] = (m[t] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [live]);
  const byPerson = useMemo(() => {
    const m = {};
    live.filter((e) => e.assigned_to).forEach((e) => {
      if (!m[e.assigned_to]) m[e.assigned_to] = { count: 0, overdue: 0, dueSoon: 0 };
      m[e.assigned_to].count += 1;
      const lvl = dueStatus(e.next_inspection_date).level;
      if (lvl === 'overdue') m[e.assigned_to].overdue += 1;
      if (lvl === 'soon') m[e.assigned_to].dueSoon += 1;
    });
    return Object.entries(m).sort((a, b) => b[1].count - a[1].count);
  }, [live]);
  const byVehicle = useMemo(() => {
    const m = {};
    live.filter((e) => e.vehicle_reg).forEach((e) => { (m[e.vehicle_reg] ||= []).push(e); });
    return m;
  }, [live]);
  const byLocation = useMemo(() => {
    const m = {};
    live.forEach((e) => { const loc = e.location || e.vehicle_reg || 'Unassigned'; (m[loc] ||= []).push(e); });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [live]);

  const uniqueTypes = useMemo(() => [...new Set(equipment.map((e) => e.equipment_type).filter(Boolean))].sort(), [equipment]);
  const uniqueManufacturers = useMemo(() => [...new Set(equipment.map((e) => e.manufacturer).filter(Boolean))].sort(), [equipment]);
  const uniqueKits = useMemo(() => [...new Set(equipment.map((e) => e.kit_id).filter(Boolean))].sort(), [equipment]);
  const uniqueTechs = useMemo(() => [...new Set(equipment.map((e) => e.assigned_to).filter(Boolean))].sort(), [equipment]);
  const uniqueVehicleRegs = useMemo(() => vehicles.map((v) => v.vehicle_reg), [vehicles]);
  const people = useMemo(() => [...new Set([...staffNames, ...uniqueTechs])].sort(), [staffNames, uniqueTechs]);

  const attentionItems = useMemo(() => live.filter((e) => e.status === 'Fail' || dueStatus(e.next_inspection_date).level === 'overdue').slice(0, 8), [live]);
  const counts = useMemo(() => ({
    total: stats?.total ?? equipment.length,
    active: stats?.active ?? equipment.filter((e) => e.status === 'Pass').length,
    dueSoon: stats?.dueSoon ?? live.filter((e) => dueStatus(e.next_inspection_date).level === 'soon').length,
    overdue: stats?.overdue ?? live.filter((e) => dueStatus(e.next_inspection_date).level === 'overdue').length,
    failed: stats?.failed ?? equipment.filter((e) => e.status === 'Fail').length,
    scrapped: stats?.scrapped ?? equipment.filter((e) => e.status === 'Scrapped').length,
  }), [stats, equipment, live]);

  // ─── Equipment CRUD ──────────────────────────
  const openAddEquip = (presets = {}) => {
    const f = { ...EMPTY_EQUIP, ...presets };
    setEquipForm(f); setEquipErrors({}); setModalError(null); setFormStep(0); setAssignNewPerson(false);
    setEquipModal({ editing: null, initial: f });
  };
  const openEditEquip = (item) => {
    const f = { ...EMPTY_EQUIP };
    Object.keys(f).forEach((k) => {
      if (item[k] != null) f[k] = DATE_KEYS.includes(k) ? toLocalISODate(item[k]) : item[k];
    });
    setEquipForm(f); setEquipErrors({}); setModalError(null); setFormStep(0); setAssignNewPerson(false);
    setEquipModal({ editing: item, initial: f });
  };
  useOpenFromLink(equipment, (e, id) => String(e.id) === id, (item) => { setDetailItem(item); });

  const setE = (k, v) => { setEquipForm((f) => ({ ...f, [k]: v })); if (equipErrors[k]) setEquipErrors((x) => ({ ...x, [k]: null })); };
  const validateEquip = () => {
    const errs = {};
    if (!equipForm.equipment_name.trim()) errs.equipment_name = 'Enter the equipment name';
    if (equipForm.last_inspection_date && equipForm.next_inspection_date && equipForm.next_inspection_date < equipForm.last_inspection_date) {
      errs.next_inspection_date = 'The next inspection must be after the last one';
    }
    setEquipErrors(errs);
    if (errs.equipment_name) setFormStep(0);
    else if (errs.next_inspection_date) setFormStep(2);
    return !Object.keys(errs).length;
  };
  const saveEquip = async () => {
    if (!validateEquip()) return;
    setSaving(true); setModalError(null);
    const editing = equipModal.editing;
    try {
      const saved = await apiFetch(editing ? `/api/contractor/equipment/${editing.id}` : '/api/contractor/equipment', {
        method: editing ? 'PUT' : 'POST', body: JSON.stringify(equipForm),
      });
      toast.success(editing ? `${equipForm.equipment_name.trim()} updated` : `${equipForm.equipment_name.trim()} added`);
      setEquipModal(null);
      await refreshAll();
      if (editing && detailItem?.id === editing.id && saved) setDetailItem(saved);
    } catch (e) { setModalError(apiErrorMessage(e, "Couldn't save the equipment")); }
    finally { setSaving(false); }
  };

  const deleteEquip = async (item) => {
    const ok = await confirm({
      title: `Delete ${item.equipment_name} permanently?`,
      body: 'Its inspection dates and history are removed. To keep the record for audit, use Scrap instead.',
      confirmLabel: 'Delete equipment', tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/contractor/equipment/${item.id}`, { method: 'DELETE' });
      toast.success(`${item.equipment_name} deleted`);
      if (detailItem?.id === item.id) setDetailItem(null);
      refreshAll();
    } catch (e) { toast.error(`Couldn't delete it: ${apiErrorMessage(e)}`); }
  };

  const scrapEquip = async (item) => {
    const ok = await confirm({
      title: `Scrap ${item.equipment_name}?`,
      body: 'It’s taken out of service and drops off inspection reminders. The record and its history are kept.',
      confirmLabel: 'Mark as scrapped', tone: 'danger',
    });
    if (!ok) return;
    try {
      const updated = await apiFetch(`/api/contractor/equipment/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'Scrapped', notes: (item.notes ? `${item.notes} | ` : '') + `Marked as scrapped ${formatDate(new Date())}` }),
      });
      toast.success(`${item.equipment_name} marked as scrapped`);
      await refreshAll();
      if (detailItem?.id === item.id && updated) setDetailItem(updated);
    } catch (e) { toast.error(`Couldn't scrap it: ${apiErrorMessage(e)}`); }
  };

  // ─── Vehicle CRUD ──────────────────────────
  const openAddVehicle = () => { setVehicleForm({ ...EMPTY_VEHICLE }); setVehicleError(null); setModalError(null); setVehicleModal({ editing: null, initial: { ...EMPTY_VEHICLE } }); };
  const openEditVehicle = (v) => {
    const f = { vehicle_reg: v.vehicle_reg || '', vehicle_name: v.vehicle_name || '', make: v.make || '', model: v.model || '', colour: v.colour || '', kit_id: v.kit_id || '', notes: v.notes || '' };
    setVehicleForm(f); setVehicleError(null); setModalError(null); setVehicleModal({ editing: v, initial: f });
  };
  const saveVehicle = async () => {
    const reg = vehicleForm.vehicle_reg.trim().toUpperCase();
    if (!reg) { setVehicleError('Enter the registration'); return; }
    setSaving(true); setModalError(null);
    const editing = vehicleModal.editing;
    try {
      await apiFetch(editing ? `/api/contractor/vehicles/${editing.id}` : '/api/contractor/vehicles', {
        method: editing ? 'PUT' : 'POST', body: JSON.stringify({ ...vehicleForm, vehicle_reg: reg }),
      });
      toast.success(editing ? `${reg} updated` : `${reg} added`);
      setVehicleModal(null); refreshAll();
    } catch (e) { setModalError(apiErrorMessage(e, "Couldn't save the vehicle")); }
    finally { setSaving(false); }
  };
  const deleteVehicle = async (v) => {
    const linked = (byVehicle[v.vehicle_reg] || []).length;
    const ok = await confirm({
      title: `Delete vehicle ${v.vehicle_reg}?`,
      body: linked ? `${plural(linked, 'item')} of equipment in it will be unlinked from the vehicle. This can’t be undone.` : 'This can’t be undone.',
      confirmLabel: 'Delete vehicle', tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/contractor/vehicles/${v.id}`, { method: 'DELETE' });
      toast.success(`${v.vehicle_reg} deleted`); setVehicleDetail(null); refreshAll();
    } catch (e) { toast.error(`Couldn't delete the vehicle: ${apiErrorMessage(e)}`); }
  };

  // ─── Bulk edit ──────────────────────────
  const openBulkEdit = () => { setBulkField('assigned_to'); setBulkValue(''); setModalError(null); setShowBulkModal(true); };
  const saveBulkEdit = async () => {
    if (!selectedIds.size) return;
    if ((bulkField === 'status' || bulkField === 'category') && !bulkValue) { setModalError(`Choose the new ${bulkField}`); return; }
    setSaving(true); setModalError(null);
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map((id) => apiFetch(`/api/contractor/equipment/${id}`, { method: 'PUT', body: JSON.stringify({ [bulkField]: bulkValue }) })));
    const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
    const ok = ids.length - failedIds.length;
    setSaving(false);
    if (!failedIds.length) {
      toast.success(`${plural(ok, 'item')} updated`);
      setShowBulkModal(false); setSelectedIds(new Set());
    } else {
      const reason = apiErrorMessage(results.find((r) => r.status === 'rejected')?.reason);
      toast.error(`${ok} of ${plural(ids.length, 'item')} updated — ${failedIds.length} failed: ${reason}. The failed items are still selected.`);
      setSelectedIds(new Set(failedIds));
      if (ok) setShowBulkModal(false);
    }
    refreshAll();
  };

  // ─── Navigation helpers ──────────────────────
  const showingSubView = detailItem || vehicleDetail || personnelDetail || locationDetail;
  const goBack = () => {
    if (detailItem) { setDetailItem(null); return; }
    if (vehicleDetail) { setVehicleDetail(null); return; }
    if (personnelDetail) { setPersonnelDetail(null); return; }
    if (locationDetail) setLocationDetail(null);
  };
  const filtersOn = filterCat !== 'All' || filterStatus !== 'All' || search;

  const rootProps = {
    ref: rootRef,
    className: inWorkspace ? 'cw-equip' : 'cw-root cw-equip',
    style: inWorkspace ? undefined : { minHeight: 0, background: 'transparent' },
  };

  if (loading && !equipment.length && !loadError) {
    return <div {...rootProps}><Loading variant="page" label="Loading equipment…" /></div>;
  }

  const columns = [
    { key: 'equipment_name', label: 'Equipment', primary: true, render: (e) => <span style={{ fontWeight: 600 }}>{e.equipment_name}</span> },
    { key: 'equipment_type', label: 'Type', render: (e) => e.equipment_type || dash },
    { key: 'id_number', label: 'ID', render: (e) => (e.id_number ? <code style={mono}>{e.id_number}</code> : dash) },
    { key: 'manufacturer', label: 'Manufacturer', mobileHide: true, render: (e) => e.manufacturer || dash },
    { key: 'safe_working_load', label: 'SWL', mobileHide: true, render: (e) => e.safe_working_load || dash },
    { key: 'assigned_to', label: 'Assigned to', render: (e) => e.assigned_to || <span className="cw-meta">Unassigned</span> },
    { key: 'kit_id', label: 'Kit', mobileHide: true, render: (e) => (e.kit_id ? <code style={mono}>{e.kit_id}</code> : dash) },
    { key: 'vehicle_reg', label: 'Vehicle', render: (e) => e.vehicle_reg || dash },
    { key: 'next_inspection_date', label: 'Next inspection', render: (e) => <InspectionDate date={e.next_inspection_date} status={e.status} /> },
    { key: 'status', label: 'Status', render: (e) => <StatusBadge status={e.status} /> },
  ];

  const statTiles = [
    { label: 'Total items', value: counts.total, icon: 'fa-solid fa-screwdriver-wrench', onClick: () => { setFilterStatus('All'); setTab('equipment'); } },
    { label: 'Pass', value: counts.active, icon: 'fa-solid fa-circle-check', onClick: () => { setFilterStatus('Pass'); setTab('equipment'); } },
    { label: 'Due in 30 days', value: counts.dueSoon, icon: 'fa-regular fa-calendar', onClick: () => { setFilterStatus('All'); setTab('equipment'); } },
    { label: 'Overdue', value: counts.overdue, icon: 'fa-solid fa-triangle-exclamation', onClick: () => { setFilterStatus('All'); setTab('equipment'); } },
    { label: 'Failed', value: counts.failed, icon: 'fa-solid fa-circle-xmark', onClick: () => { setFilterStatus('Fail'); setTab('equipment'); } },
  ];

  const title = detailItem ? detailItem.equipment_name
    : vehicleDetail ? `Vehicle ${vehicleDetail.vehicle_reg}`
      : personnelDetail || (locationDetail ? `Location: ${locationDetail}` : 'Equipment & fleet');

  const detailFields = (pairs) => (
    <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 14, margin: 0 }}>
      {pairs.map(([label, value]) => (
        <div key={label}>
          <dt className="cw-label">{label}</dt>
          <dd style={{ margin: '2px 0 0', fontWeight: 600, overflowWrap: 'anywhere' }}>{value || dash}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <div {...rootProps}>
      <header className="cw-listhead">
        <div className="cw-listhead-title">
          {showingSubView && (
            <button type="button" className="cw-btn-link" onClick={goBack} style={{ marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <i className="fa-solid fa-chevron-left" aria-hidden="true" /> Back
            </button>
          )}
          <h2 className="cw-title">{title}</h2>
          {!showingSubView && <p className="cw-subtle">Equipment, vehicles and who holds what — with inspection dates</p>}
        </div>
        <div className="cw-listhead-actions">
          <button type="button" className="cw-btn cw-btn-primary" onClick={() => openAddEquip()}><i className="fa-solid fa-plus" aria-hidden="true" /> Add equipment</button>
        </div>
      </header>
      {!showingSubView && (
        <div className="cw-toolbar">
          <TabBar tabs={TABS} active={tab} onChange={setTab} ariaLabel="Equipment sections" idBase="cw-equip" className="cw-toolbar-tabs" />
        </div>
      )}

      <div {...(!showingSubView ? tabPanelProps('cw-equip', tab) : {})} key={showingSubView ? 'sub' : tab} className="cw-swap" style={{ padding: '16px 24px 24px', maxWidth: 1440, display: 'grid', gap: 16, minWidth: 0 }}>
        {loadError && (
          <ErrorState thing="your equipment" error={loadError} onRetry={reload} retrying={loading} />
        )}

        {/* ─── OVERVIEW ─────────────────────────── */}
        {!loadError && tab === 'overview' && !showingSubView && (
          <>
            <ul className="cw-tiles" style={{ padding: 0, gridTemplateColumns: `repeat(${Math.min(statTiles.length, 5)}, minmax(0, 1fr))` }} aria-label="Equipment at a glance">
              {statTiles.map((s) => (
                <li key={s.label}>
                  <button type="button" onClick={s.onClick} className={`cw-tile${/overdue|failed/i.test(s.label) && Number(s.value) ? ' warn' : ''}`}>
                    <span className="cw-tile-ico"><i className={s.icon} aria-hidden="true" /></span>
                    <span className="cw-tile-n"><CountUp value={s.value} /></span>
                    <span className="cw-tile-label">{s.label}</span>
                  </button>
                </li>
              ))}
            </ul>

            {attentionItems.length > 0 && (
              <section className="cw-card" style={{ overflow: 'hidden' }} aria-labelledby="cw-eq-attn">
                <div className="cw-card-head"><h3 id="cw-eq-attn"><i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--cw-danger)', marginRight: 8 }} aria-hidden="true" />Needs attention</h3></div>
                <ItemList items={attentionItems} onOpen={setDetailItem} empty="Nothing needs attention" />
              </section>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
              <section className="cw-card" style={{ overflow: 'hidden' }} aria-labelledby="cw-eq-loc">
                <div className="cw-card-head"><h3 id="cw-eq-loc">Locations</h3></div>
                {byLocation.length === 0 ? <EmptyState compact title="No equipment yet" /> : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {byLocation.slice(0, 6).map(([loc, items]) => (
                      <li key={loc}>
                        <button type="button" style={rowBtn} onClick={() => setLocationDetail(loc)}>
                          <span style={{ flex: 1 }}>{loc}</span><Chip tone="neutral" label={plural(items.length, 'item')} />
                          <i className="fa-solid fa-chevron-right" aria-hidden="true" style={{ color: 'var(--cw-placeholder)' }} />
                        </button>
                      </li>
                    ))}
                    {byLocation.length > 6 && <li className="cw-meta" style={{ padding: '10px 16px' }}>+{byLocation.length - 6} more locations</li>}
                  </ul>
                )}
              </section>

              <section className="cw-card" style={{ overflow: 'hidden' }} aria-labelledby="cw-eq-veh">
                <div className="cw-card-head">
                  <h3 id="cw-eq-veh">Vehicles</h3>
                  <button type="button" className="cw-btn-link" onClick={() => setTab('vehicles')}>View all</button>
                </div>
                {vehiclesError ? <ErrorState compact thing="vehicles" error={vehiclesError} onRetry={reload} />
                  : vehicles.length === 0 ? (
                    <EmptyState compact title="No vehicles registered" actionLabel="Add vehicle" onAction={openAddVehicle} />
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {vehicles.slice(0, 5).map((v) => (
                        <li key={v.id}>
                          <button type="button" style={rowBtn} onClick={() => setVehicleDetail(v)}>
                            <i className="fa-solid fa-truck" aria-hidden="true" style={{ color: 'var(--cw-text-muted)' }} />
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontWeight: 600 }}>{v.vehicle_reg}</span>
                              <span className="cw-meta">{v.vehicle_name || [v.make, v.model].filter(Boolean).join(' ') || 'No description'}</span>
                            </span>
                            <Chip tone="neutral" label={plural((byVehicle[v.vehicle_reg] || []).length, 'item')} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
              </section>

              <section className="cw-card" style={{ overflow: 'hidden' }} aria-labelledby="cw-eq-ppl">
                <div className="cw-card-head">
                  <h3 id="cw-eq-ppl">Personnel</h3>
                  <button type="button" className="cw-btn-link" onClick={() => setTab('personnel')}>View all</button>
                </div>
                {byPerson.length === 0 ? <EmptyState compact title="No equipment assigned yet" /> : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {byPerson.slice(0, 5).map(([name, info]) => (
                      <li key={name}>
                        <button type="button" style={rowBtn} onClick={() => setPersonnelDetail(name)}>
                          <Avatar name={name} size={30} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontWeight: 600 }}>{name}</span>
                            <span className="cw-meta">{plural(info.count, 'item')}{info.overdue > 0 ? ` · ${info.overdue} overdue` : ''}</span>
                          </span>
                          <i className="fa-solid fa-chevron-right" aria-hidden="true" style={{ color: 'var(--cw-placeholder)' }} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {byType.length > 0 && (
              <section className="cw-card cw-card-pad" aria-labelledby="cw-eq-types">
                <h3 id="cw-eq-types" className="cw-h3" style={{ marginBottom: 10 }}>Equipment by type</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {byType.map(([type, count]) => (
                    <button type="button" key={type} className="cw-btn cw-btn-secondary" onClick={() => { setSearch(type); setTab('equipment'); }} aria-label={`Show ${plural(count, type)} in the list`}>
                      {type} <Chip tone="neutral" label={String(count)} />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {counts.scrapped > 0 && (
              <button type="button" className="cw-btn-link" onClick={() => { setFilterStatus('Scrapped'); setTab('equipment'); }}>
                {plural(counts.scrapped, 'scrapped item')} kept on record — view them
              </button>
            )}
          </>
        )}

        {/* ─── EQUIPMENT LIST ───────────────────── */}
        {!loadError && tab === 'equipment' && !showingSubView && (
          <>
            <div role="search" aria-label="Filter equipment" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Field label="Search" style={{ flex: '1 1 260px', margin: 0 }}>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, ID, vehicle reg, location, serial, manufacturer…" />
              </Field>
              <Field label="Category" style={{ flex: '0 1 180px', margin: 0 }}>
                <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
                  <option value="All">All categories</option>
                  {CATEGORIES.map((c) => <option key={c} value={CATEGORY_VALUES[c]}>{c}</option>)}
                </select>
              </Field>
              <Field label="Status" style={{ flex: '0 1 180px', margin: 0 }}>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="All">All statuses</option>
                  {STATUS_VALUES.map((s) => <option key={s} value={s}>{STATUS_CHIP[s][1]}</option>)}
                </select>
              </Field>
              {filtersOn && <button type="button" className="cw-btn-link" style={{ marginBottom: 12 }} onClick={() => { setSearch(''); setFilterCat('All'); setFilterStatus('All'); }}>Clear filters</button>}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className="cw-meta" aria-live="polite">{plural(filtered.length, 'item')}</span>
              {selectedIds.size > 0 && (
                <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
                  <strong>{selectedIds.size} selected</strong>
                  <button type="button" className="cw-btn cw-btn-primary" onClick={openBulkEdit}><i className="fa-solid fa-pen" aria-hidden="true" /> Bulk edit</button>
                  <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setSelectedIds(new Set())}>Clear selection</button>
                </span>
              )}
            </div>

            <div className="cw-card" style={{ overflow: 'hidden' }}>
              <DataTable
                caption="Equipment"
                columns={columns}
                rows={filtered}
                minWidth={1100}
                selectable
                selectedKeys={selectedIds}
                onSelectionChange={setSelectedIds}
                getRowKey={(e) => e.id}
                getRowLabel={(e) => e.equipment_name}
                onRowClick={setDetailItem}
                emptyState={equipment.length === 0 ? (
                  <EmptyState icon="fa-solid fa-screwdriver-wrench" title="No equipment yet" body="Add your first piece of equipment to start tracking its inspections."
                    actionLabel="Add equipment" onAction={() => openAddEquip()} />
                ) : (
                  <EmptyState compact icon="fa-solid fa-filter" title="No equipment matches these filters" actionLabel="Clear filters" onAction={() => { setSearch(''); setFilterCat('All'); setFilterStatus('All'); }} />
                )}
              />
            </div>
          </>
        )}

        {/* ─── VEHICLES ─────────────────────────── */}
        {!loadError && tab === 'vehicles' && !showingSubView && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <span className="cw-meta">{plural(vehicles.length, 'vehicle')}</span>
              <button type="button" className="cw-btn cw-btn-primary" onClick={openAddVehicle}><i className="fa-solid fa-plus" aria-hidden="true" /> Add vehicle</button>
            </div>
            {vehiclesError ? <ErrorState thing="vehicles" error={vehiclesError} onRetry={reload} />
              : vehicles.length === 0 ? (
                <EmptyState icon="fa-solid fa-truck" title="No vehicles registered" body="Add your company vehicles to track which equipment is in each one." actionLabel="Add vehicle" onAction={openAddVehicle} />
              ) : (
                <ul style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 12, listStyle: 'none', margin: 0, padding: 0 }}>
                  {vehicles.map((v) => (
                    <li key={v.id} className="cw-card cw-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <i className="fa-solid fa-truck" aria-hidden="true" style={{ color: 'var(--cw-blue)', fontSize: '1.2rem' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <button type="button" className="cw-btn-link" onClick={() => setVehicleDetail(v)} style={{ fontSize: 'var(--cs-text-md)' }}>{v.vehicle_reg}</button>
                          <div className="cw-meta">{v.vehicle_name || [v.make, v.model].filter(Boolean).join(' ') || 'No description'}</div>
                        </div>
                        <button type="button" className="cw-icon-btn" aria-label={`Edit vehicle ${v.vehicle_reg}`} title="Edit" onClick={() => openEditVehicle(v)}><i className="fa-solid fa-pen" aria-hidden="true" /></button>
                        <button type="button" className="cw-icon-btn" aria-label={`Delete vehicle ${v.vehicle_reg}`} title="Delete" onClick={() => deleteVehicle(v)} style={{ color: 'var(--cw-danger)' }}><i className="fa-solid fa-trash" aria-hidden="true" /></button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {v.colour && <Chip tone="neutral" label={v.colour} />}
                        {v.kit_id && <Chip tone="neutral" label={`Kit ${v.kit_id}`} />}
                        <Chip tone="info" label={plural((byVehicle[v.vehicle_reg] || []).length, 'item')} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
          </>
        )}

        {/* ─── PERSONNEL ────────────────────────── */}
        {!loadError && tab === 'personnel' && !showingSubView && (
          <>
            <span className="cw-meta">{plural(byPerson.length, 'person', 'people')} with equipment assigned</span>
            {byPerson.length === 0 ? (
              <EmptyState icon="fa-solid fa-user" title="No equipment assigned" body="Use the “Assigned to” field when adding or editing equipment." />
            ) : (
              <ul style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: 12, listStyle: 'none', margin: 0, padding: 0 }}>
                {byPerson.map(([name, info]) => (
                  <li key={name}>
                    <button type="button" className="cw-card cw-card-pad" onClick={() => setPersonnelDetail(name)}
                      style={{ width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'grid', gap: 8 }}>
                      <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <Avatar name={name} size={36} />
                        <span>
                          <span style={{ display: 'block', fontWeight: 700 }}>{name}</span>
                          <span className="cw-meta">{plural(info.count, 'item')} assigned</span>
                        </span>
                      </span>
                      {(info.overdue > 0 || info.dueSoon > 0) && (
                        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {info.overdue > 0 && <Chip tone="danger" label={`${info.overdue} overdue`} />}
                          {info.dueSoon > 0 && <Chip tone="warning" label={`${info.dueSoon} due soon`} />}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {/* ─── EQUIPMENT DETAIL ─────────────────── */}
        {detailItem && (
          <section className="cw-card" style={{ overflow: 'hidden' }} aria-label={detailItem.equipment_name}>
            <div className="cw-card-head" style={{ flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <StatusBadge status={detailItem.status} />
                <span className="cw-meta">{[detailItem.equipment_type || 'Equipment', CATEGORY_LABEL[detailItem.category] || detailItem.category].filter(Boolean).join(' · ')}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="cw-btn cw-btn-secondary" onClick={() => openEditEquip(detailItem)}><i className="fa-solid fa-pen" aria-hidden="true" /> Edit</button>
                {detailItem.status !== 'Scrapped' && <button type="button" className="cw-btn cw-btn-secondary" onClick={() => scrapEquip(detailItem)}><i className="fa-solid fa-ban" aria-hidden="true" /> Scrap</button>}
                <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => deleteEquip(detailItem)}><i className="fa-solid fa-trash" aria-hidden="true" /> Delete</button>
              </div>
            </div>
            <div className="cw-card-pad" style={{ display: 'grid', gap: 14 }}>
              {detailFields([
                ['ID number', detailItem.id_number], ['Manufacturer', detailItem.manufacturer],
                ['Safe working load', detailItem.safe_working_load], ['Serial / batch', detailItem.batch_serial_number],
                ['Assigned to', detailItem.assigned_to], ['Kit ID', detailItem.kit_id],
                ['Vehicle', detailItem.vehicle_reg], ['Colour', detailItem.colour],
                ['Location', detailItem.location], ['In service since', detailItem.date_in_service ? formatDate(detailItem.date_in_service) : null],
                ['Last inspection', detailItem.last_inspection_date ? formatDate(detailItem.last_inspection_date) : null],
                ['Next inspection', detailItem.next_inspection_date ? <InspectionDate date={detailItem.next_inspection_date} status={detailItem.status} /> : null],
                ['PAT test date', detailItem.pat_test_date ? formatDate(detailItem.pat_test_date) : null], ['CoC reference', detailItem.coc_reference],
              ])}
              {detailItem.notes && (
                <div><div className="cw-label">Notes</div><div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>{detailItem.notes}</div></div>
              )}
            </div>
          </section>
        )}

        {/* ─── VEHICLE DETAIL ───────────────────── */}
        {vehicleDetail && (
          <>
            <section className="cw-card" style={{ overflow: 'hidden' }} aria-label={`Vehicle ${vehicleDetail.vehicle_reg}`}>
              <div className="cw-card-head" style={{ flexWrap: 'wrap', gap: 10 }}>
                <h3><i className="fa-solid fa-truck" aria-hidden="true" style={{ marginRight: 8 }} />Details</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="cw-btn cw-btn-secondary" onClick={() => openEditVehicle(vehicleDetail)}><i className="fa-solid fa-pen" aria-hidden="true" /> Edit</button>
                  <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => deleteVehicle(vehicleDetail)}><i className="fa-solid fa-trash" aria-hidden="true" /> Delete</button>
                </div>
              </div>
              <div className="cw-card-pad">
                {detailFields([
                  ['Name', vehicleDetail.vehicle_name], ['Make', vehicleDetail.make], ['Model', vehicleDetail.model],
                  ['Colour', vehicleDetail.colour], ['Kit ID', vehicleDetail.kit_id], ['Notes', vehicleDetail.notes],
                ])}
              </div>
            </section>
            <section className="cw-card" style={{ overflow: 'hidden' }} aria-labelledby="cw-eq-inveh">
              <div className="cw-card-head">
                <h3 id="cw-eq-inveh">Equipment in this vehicle</h3>
                <button type="button" className="cw-btn cw-btn-primary" onClick={() => openAddEquip({ vehicle_reg: vehicleDetail.vehicle_reg })}><i className="fa-solid fa-plus" aria-hidden="true" /> Add to vehicle</button>
              </div>
              <ItemList items={byVehicle[vehicleDetail.vehicle_reg] || []} onOpen={(i) => { setVehicleDetail(null); setDetailItem(i); }} showDue={false} empty="No equipment in this vehicle" />
            </section>
          </>
        )}

        {/* ─── PERSONNEL DETAIL ─────────────────── */}
        {personnelDetail && (
          <section className="cw-card" style={{ overflow: 'hidden' }} aria-label={personnelDetail}>
            <div className="cw-card-head">
              <h3 style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Avatar name={personnelDetail} size={28} /> Assigned equipment</h3>
              <button type="button" className="cw-btn cw-btn-primary" onClick={() => openAddEquip({ assigned_to: personnelDetail })}><i className="fa-solid fa-plus" aria-hidden="true" /> Assign equipment</button>
            </div>
            <ItemList items={live.filter((e) => e.assigned_to === personnelDetail)} onOpen={(i) => { setPersonnelDetail(null); setDetailItem(i); }} empty="No equipment assigned" />
          </section>
        )}

        {/* ─── LOCATION DETAIL ──────────────────── */}
        {locationDetail && (
          <section className="cw-card" style={{ overflow: 'hidden' }} aria-label={`Location ${locationDetail}`}>
            <div className="cw-card-head">
              <h3>Equipment here</h3>
              <button type="button" className="cw-btn cw-btn-primary" onClick={() => openAddEquip({ location: locationDetail === 'Unassigned' ? '' : locationDetail })}><i className="fa-solid fa-plus" aria-hidden="true" /> Add equipment here</button>
            </div>
            <ItemList items={byLocation.find(([l]) => l === locationDetail)?.[1] || []} onOpen={(i) => { setLocationDetail(null); setDetailItem(i); }} empty="No equipment at this location" />
          </section>
        )}
      </div>

      {/* ─── EQUIPMENT ADD / EDIT ─────────────── */}
      {equipModal && (
        <Modal
          title={equipModal.editing ? `Edit ${equipModal.editing.equipment_name}` : 'Add equipment'}
          maxWidth={680}
          dirty={JSON.stringify(equipForm) !== JSON.stringify(equipModal.initial)}
          onClose={() => !saving && setEquipModal(null)}
          footer={<>
            {formStep > 0 && <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setFormStep((s) => s - 1)}>Back</button>}
            <span style={{ flex: 1 }} />
            <CancelButton disabled={saving} />
            {formStep < 2 ? (
              <button type="button" className="cw-btn cw-btn-primary" onClick={() => {
                if (formStep === 0 && !equipForm.equipment_name.trim()) { setEquipErrors({ equipment_name: 'Enter the equipment name' }); return; }
                setFormStep((s) => s + 1);
              }}>Next</button>
            ) : (
              <button type="button" className="cw-btn cw-btn-primary" onClick={saveEquip} disabled={saving} aria-busy={saving || undefined}>
                {saving ? 'Saving…' : equipModal.editing ? 'Save changes' : 'Add equipment'}
              </button>
            )}
          </>}
        >
          <FormError message={modalError} />
          <ol aria-label="Steps" style={{ display: 'flex', gap: 8, listStyle: 'none', margin: '0 0 14px', padding: 0, flexWrap: 'wrap' }}>
            {['Basic info', 'Assignment', 'Inspection & status'].map((label, i) => (
              <li key={label}>
                <button type="button" aria-current={formStep === i ? 'step' : undefined}
                  className={`cw-btn ${formStep === i ? 'cw-btn-primary' : 'cw-btn-secondary'}`}
                  onClick={() => { if (i === 0 || equipForm.equipment_name.trim()) setFormStep(i); else setEquipErrors({ equipment_name: 'Enter the equipment name' }); }}>
                  {formStep > i ? <i className="fa-solid fa-check" aria-hidden="true" /> : <span aria-hidden="true">{i + 1}.</span>} {label}
                </button>
              </li>
            ))}
          </ol>

          {formStep === 0 && (
            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Equipment name" required error={equipErrors.equipment_name}>
                <input value={equipForm.equipment_name} onChange={(e) => setE('equipment_name', e.target.value)} placeholder="e.g. Karabiner, 100 m rope, fire extinguisher" autoFocus />
              </Field>
              <div style={grid2}>
                <Field label="Equipment type">
                  <input value={equipForm.equipment_type} onChange={(e) => setE('equipment_type', e.target.value)} placeholder="e.g. Connector, descender" list="cw-eq-types" />
                </Field>
                <datalist id="cw-eq-types">{uniqueTypes.map((t) => <option key={t} value={t} />)}</datalist>
                <Field label="Category">
                  <select value={equipForm.category} onChange={(e) => setE('category', e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c} value={CATEGORY_VALUES[c]}>{c}</option>)}
                    {equipForm.category && !Object.values(CATEGORY_VALUES).includes(equipForm.category) && <option value={equipForm.category}>{equipForm.category}</option>}
                  </select>
                </Field>
                <Field label="ID number"><input value={equipForm.id_number} onChange={(e) => setE('id_number', e.target.value)} placeholder="e.g. A55 P2" /></Field>
                <Field label="Manufacturer">
                  <input value={equipForm.manufacturer} onChange={(e) => setE('manufacturer', e.target.value)} placeholder="e.g. Petzl" list="cw-eq-mfr" />
                </Field>
                <datalist id="cw-eq-mfr">{uniqueManufacturers.map((m) => <option key={m} value={m} />)}</datalist>
                <Field label="Safe working load"><input value={equipForm.safe_working_load} onChange={(e) => setE('safe_working_load', e.target.value)} placeholder="e.g. 440 kg" /></Field>
                <Field label="Serial / batch number"><input value={equipForm.batch_serial_number} onChange={(e) => setE('batch_serial_number', e.target.value)} /></Field>
                <Field label="Colour"><input value={equipForm.colour} onChange={(e) => setE('colour', e.target.value)} placeholder="e.g. Blue/silver" /></Field>
                <Field label="In service since"><input type="date" value={equipForm.date_in_service} onChange={(e) => setE('date_in_service', e.target.value)} /></Field>
              </div>
            </div>
          )}

          {formStep === 1 && (
            <div style={{ display: 'grid', gap: 12 }}>
              <p className="cw-meta" style={{ margin: 0 }}>Assign this equipment to a person, kit or vehicle.</p>
              {assignNewPerson || (equipForm.assigned_to && !people.includes(equipForm.assigned_to)) ? (
                <Field label="Assigned to" hint="A person who isn’t on your staff list">
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="cw-input" value={equipForm.assigned_to} onChange={(e) => setE('assigned_to', e.target.value)} placeholder="Their name" autoFocus style={{ flex: 1 }} />
                    <button type="button" className="cw-btn cw-btn-secondary" onClick={() => { setAssignNewPerson(false); setE('assigned_to', ''); }}>Pick from staff</button>
                  </div>
                </Field>
              ) : (
                <Field label="Assigned to">
                  <select value={equipForm.assigned_to}
                    onChange={(e) => { if (e.target.value === '__new__') { setAssignNewPerson(true); setE('assigned_to', ''); } else setE('assigned_to', e.target.value); }}>
                    <option value="">Unassigned</option>
                    {people.map((p) => <option key={p} value={p}>{p}</option>)}
                    <option value="__new__">Someone not on the staff list…</option>
                  </select>
                </Field>
              )}
              <div style={grid2}>
                <Field label="Kit ID"><input value={equipForm.kit_id} onChange={(e) => setE('kit_id', e.target.value)} placeholder="e.g. AV17" list="cw-eq-kit" /></Field>
                <datalist id="cw-eq-kit">{uniqueKits.map((k) => <option key={k} value={k} />)}</datalist>
                <Field label="Vehicle"><input value={equipForm.vehicle_reg} onChange={(e) => setE('vehicle_reg', e.target.value.toUpperCase())} placeholder="e.g. WV67 LUR" list="cw-eq-veh" /></Field>
                <datalist id="cw-eq-veh">{uniqueVehicleRegs.map((v) => <option key={v} value={v} />)}</datalist>
              </div>
              <Field label="Location"><input value={equipForm.location} onChange={(e) => setE('location', e.target.value)} placeholder="e.g. Stores, yard, job site" /></Field>
            </div>
          )}

          {formStep === 2 && (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={grid2}>
                <Field label="Status">
                  <select value={equipForm.status} onChange={(e) => setE('status', e.target.value)}>
                    {STATUS_VALUES.map((s) => <option key={s} value={s}>{STATUS_CHIP[s][1]}</option>)}
                  </select>
                </Field>
                <Field label="PAT test date"><input type="date" value={equipForm.pat_test_date} onChange={(e) => setE('pat_test_date', e.target.value)} /></Field>
                <Field label="Last inspection"><input type="date" value={equipForm.last_inspection_date} onChange={(e) => setE('last_inspection_date', e.target.value)} /></Field>
                <Field label="Next inspection" error={equipErrors.next_inspection_date}><input type="date" min={equipForm.last_inspection_date || undefined} value={equipForm.next_inspection_date} onChange={(e) => setE('next_inspection_date', e.target.value)} /></Field>
              </div>
              <Field label="Notes"><textarea value={equipForm.notes} onChange={(e) => setE('notes', e.target.value)} rows={3} style={{ resize: 'vertical' }} /></Field>
              <Field label="Certificate of conformity reference"><input value={equipForm.coc_reference} onChange={(e) => setE('coc_reference', e.target.value)} /></Field>
            </div>
          )}
        </Modal>
      )}

      {/* ─── VEHICLE ADD / EDIT ───────────────── */}
      {vehicleModal && (
        <Modal title={vehicleModal.editing ? `Edit ${vehicleModal.editing.vehicle_reg}` : 'Add vehicle'} maxWidth={560}
          dirty={JSON.stringify(vehicleForm) !== JSON.stringify(vehicleModal.initial)} onClose={() => !saving && setVehicleModal(null)}
          footer={<>
            <CancelButton disabled={saving} />
            <button type="button" className="cw-btn cw-btn-primary" onClick={saveVehicle} disabled={saving} aria-busy={saving || undefined}>{saving ? 'Saving…' : vehicleModal.editing ? 'Save changes' : 'Add vehicle'}</button>
          </>}>
          <FormError message={modalError} />
          <form noValidate onSubmit={(e) => { e.preventDefault(); saveVehicle(); }} style={{ display: 'grid', gap: 12 }}>
            <Field label="Registration" required error={vehicleError}>
              <input value={vehicleForm.vehicle_reg} onChange={(e) => { setVehicleForm((f) => ({ ...f, vehicle_reg: e.target.value.toUpperCase() })); setVehicleError(null); }} placeholder="e.g. WV67 LUR" autoFocus autoCapitalize="characters" />
            </Field>
            <Field label="Name / description"><input value={vehicleForm.vehicle_name} onChange={(e) => setVehicleForm((f) => ({ ...f, vehicle_name: e.target.value }))} placeholder="e.g. Main van" /></Field>
            <div style={grid2}>
              <Field label="Make"><input value={vehicleForm.make} onChange={(e) => setVehicleForm((f) => ({ ...f, make: e.target.value }))} placeholder="e.g. Ford" /></Field>
              <Field label="Model"><input value={vehicleForm.model} onChange={(e) => setVehicleForm((f) => ({ ...f, model: e.target.value }))} placeholder="e.g. Transit" /></Field>
              <Field label="Colour"><input value={vehicleForm.colour} onChange={(e) => setVehicleForm((f) => ({ ...f, colour: e.target.value }))} /></Field>
              <Field label="Kit ID"><input value={vehicleForm.kit_id} onChange={(e) => setVehicleForm((f) => ({ ...f, kit_id: e.target.value }))} /></Field>
            </div>
            <Field label="Notes"><textarea value={vehicleForm.notes} onChange={(e) => setVehicleForm((f) => ({ ...f, notes: e.target.value }))} rows={2} style={{ resize: 'vertical' }} /></Field>
          </form>
        </Modal>
      )}

      {/* ─── BULK EDIT ────────────────────────── */}
      {showBulkModal && (
        <Modal title={`Bulk edit ${plural(selectedIds.size, 'item')}`} maxWidth={520} dirty={!!bulkValue} onClose={() => !saving && setShowBulkModal(false)}
          footer={<>
            <CancelButton disabled={saving} />
            <button type="button" className="cw-btn cw-btn-primary" onClick={saveBulkEdit} disabled={saving} aria-busy={saving || undefined}>{saving ? 'Updating…' : `Update ${plural(selectedIds.size, 'item')}`}</button>
          </>}>
          <FormError message={modalError} />
          <p className="cw-meta" style={{ margin: '0 0 12px' }}>The new value replaces the existing value on every selected item.</p>
          <Field label="Field to update">
            <select value={bulkField} onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); setModalError(null); }}>
              <option value="assigned_to">Assigned to</option>
              <option value="vehicle_reg">Vehicle</option>
              <option value="location">Location</option>
              <option value="kit_id">Kit ID</option>
              <option value="status">Status</option>
              <option value="category">Category</option>
            </select>
          </Field>
          <Field label="New value" hint={['assigned_to', 'vehicle_reg', 'location', 'kit_id'].includes(bulkField) ? 'Leave empty to clear it' : undefined}>
            {bulkField === 'status' ? (
              <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                <option value="">Choose a status…</option>
                {STATUS_VALUES.map((s) => <option key={s} value={s}>{STATUS_CHIP[s][1]}</option>)}
              </select>
            ) : bulkField === 'category' ? (
              <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                <option value="">Choose a category…</option>
                {CATEGORIES.map((c) => <option key={c} value={CATEGORY_VALUES[c]}>{c}</option>)}
              </select>
            ) : (
              <input value={bulkValue} onChange={(e) => setBulkValue(bulkField === 'vehicle_reg' ? e.target.value.toUpperCase() : e.target.value)}
                list={bulkField === 'assigned_to' ? 'cw-eq-bulk-ppl' : bulkField === 'vehicle_reg' ? 'cw-eq-bulk-veh' : bulkField === 'kit_id' ? 'cw-eq-bulk-kit' : undefined} />
            )}
          </Field>
          <datalist id="cw-eq-bulk-ppl">{people.map((t) => <option key={t} value={t} />)}</datalist>
          <datalist id="cw-eq-bulk-veh">{uniqueVehicleRegs.map((v) => <option key={v} value={v} />)}</datalist>
          <datalist id="cw-eq-bulk-kit">{uniqueKits.map((k) => <option key={k} value={k} />)}</datalist>
        </Modal>
      )}
    </div>
  );
}
